import { RouteProp, useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { auth, logger, SMSInputScreen, useAlert, useTheme } from "capsule"
import { FirebaseAuthTypes, multiFactor, PhoneAuthProvider } from "capsule/features/Firebase"
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet } from "react-native"

import { INPUT_HEIGHT, SMS_CODE_LENGTH } from "../../features/config/Constants"
import { SMSValidationNS } from "../../features/i18n/constants"
import { RootParamList } from "../../features/Navigation/types"
import crashlytics from "../../services/Analytics/crashlytics"

interface IProps {
  route: RouteProp<RootParamList, "MfaValidation">
}

const MfaValidationScreen: FC<IProps> = ({
  route: {
    params: { phoneNumber },
  },
}) => {
  const {
    dimensions: { spacing },
  } = useTheme()
  const { t } = useTranslation(SMSValidationNS)
  const navigation = useNavigation<StackNavigationProp<RootParamList, "MfaRegister">>()
  const { showSnack } = useAlert()
  const [verifId, setVerifId] = useState("")
  const [multiFactorUser, setMultifactorUser] = useState<FirebaseAuthTypes.MultiFactorUser>()
  // If null, no SMS has been sent - only onBoarding
  const recaptchaRef = useRef(null)
  const [editable, setEditable] = useState(false)
  const handleMultifactor = useCallback(async () => {
    try {
      setEditable(false)
      const authTemp = auth()
      let recaptchaVerifier
      if (Platform.OS === "web" && recaptchaRef.current) {
        // @ts-ignore
        recaptchaVerifier = authTemp.RecaptchaVerifier(recaptchaRef.current, {
          size: "invisible",
          callback(response) {
            logger(response)
          },
        })
        await recaptchaVerifier.render()
      }
      const tempUser = authTemp.currentUser
      if (!tempUser) {
        showSnack({ message: t("mfaError") })
        return
      }
      if (tempUser?.multiFactor) {
        const multiFactorUserTemp = await multiFactor(tempUser)
        const multifactorSession = await multiFactorUserTemp.getSession()
        setMultifactorUser(multiFactorUserTemp)
        const phoneInfoOptions = {
          phoneNumber,
          session: multifactorSession,
        }
        const verificationId = await new PhoneAuthProvider(authTemp).verifyPhoneNumber(
          phoneInfoOptions,
          Platform.OS === "web" ? recaptchaVerifier : undefined,
        )
        setVerifId(verificationId)

        setEditable(true)
      }
    } catch (e) {
      if (e.code === "auth/requires-recent-login") {
        navigation.navigate("MfaRegister")
        return
      }
      crashlytics.reportError(10, `multifactor error : ${e.message}`)
      logger("Multifactor error: ", e)
      showSnack({ message: t("mfaError") })
    } finally {
      setEditable(true)
    }
  }, [navigation, phoneNumber, showSnack, t])
  useEffect(() => {
    handleMultifactor()
  }, [handleMultifactor])

  const { inputStyle } = useMemo(
    () => ({
      inputStyle: [
        styles.input,
        {
          marginTop: spacing,
        },
      ],
    }),
    [spacing],
  )

  // Launch when code is submitted from textInput by user
  const onValidate = useCallback(
    async (code: string) => {
      if (code.length !== SMS_CODE_LENGTH) {
        return
      }
      if (!verifId) {
        showSnack({ message: t("mfaError") })
        return
      }
      try {
        const cred = auth.PhoneAuthProvider.credential(verifId, code)
        const multiFactorAssertion = auth.PhoneMultiFactorGenerator.assertion(cred)
        if (multiFactorUser) {
          await multiFactorUser.enroll(multiFactorAssertion, t("mfaText"))
        }
      } catch (e) {
        if (e.code === "auth/requires-recent-login") {
          navigation.navigate("MfaRegister")
          return
        }
        crashlytics.reportError(10, `confirmation code error : ${e.message}`)
        logger("Confirmation code error: ", e)
        showSnack({ message: t("codeError") })
      } finally {
        setEditable(true)
      }
    },
    [multiFactorUser, navigation, showSnack, t, verifId],
  )

  return (
    <SMSInputScreen
      phone={phoneNumber}
      {...{
        onValidate,
        inputStyle,
        length: SMS_CODE_LENGTH,
        editable,
        onResend: handleMultifactor,
      }}
      extraComponent={
        Platform.OS === "web" ? <div id="recaptcha-container" ref={recaptchaRef} /> : undefined
      }
    />
  )
}

const styles = StyleSheet.create({
  input: {
    height: INPUT_HEIGHT,
  },
})

export default MfaValidationScreen
